import { z } from "zod";

import { WellKnown } from "../well-known.js";
import type { Currency } from "./api/trade-preferences.js";

export const ALL_MARKETPLACES = ["cardtrader", "tcgplayer", "cardmarket", "cardnexus"] as const;

// Lives here, not in schemas.ts, because schemas.ts imports from types/ and a
// definition there would cycle.
export const marketplaceEnum = z.enum(ALL_MARKETPLACES);

export type Marketplace = z.infer<typeof marketplaceEnum>;

export const EUR_MARKETPLACES: ReadonlySet<Marketplace> = new Set([
  "cardmarket",
  "cardtrader",
  "cardnexus",
]);

// Prices on the wire are integer cents with no currency of their own.
export const MARKETPLACE_CURRENCY: Record<Marketplace, Currency> = {
  tcgplayer: "USD",
  cardmarket: "EUR",
  cardtrader: "EUR",
  cardnexus: "EUR",
};

// TCGplayer is a US storefront carrying English stock only; its price guide is
// language-aggregate, so a SKU must never bind to a non-English printing.
export const MARKETPLACE_PRINTING_LANGUAGES: Record<Marketplace, ReadonlySet<string> | null> = {
  tcgplayer: new Set([WellKnown.language.EN]),
  cardmarket: null,
  cardtrader: null,
  cardnexus: null,
};

export const LANGUAGE_KEYED_MARKETPLACES: ReadonlySet<Marketplace> = new Set([
  "cardtrader",
  "cardnexus",
]);

export function marketplaceCarriesLanguage(marketplace: Marketplace, language: string): boolean {
  const carried = MARKETPLACE_PRINTING_LANGUAGES[marketplace];
  return carried === null || carried.has(language);
}

export const TIME_RANGE_DAYS = {
  "7d": 7,
  "30d": 30,
  "90d": 90,
  all: null,
} as const;

export type TimeRange = keyof typeof TIME_RANGE_DAYS;
